import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import type {
  QuoteStatus,
  ShipmentDetail,
} from '#/shared/api/generated/shipment/models'
import {
  getGetShipmentApiV1ShipmentsShipmentIdGetQueryKey,
  useBackToQuoteApiV1ShipmentsShipmentIdBackToQuotePost,
} from '#/shared/api/generated/shipment/theCargoShipment'
import { useAbility, useScopeOf } from '#/shared/lib/ability'
import {
  getApiErrorCode,
  getApiErrorDetail,
} from '#/shared/lib/api/error-handler'
import { useAuthStore } from '#/shared/lib/auth'
import { toast } from '#/shared/ui/sonner'

import { SHIPMENT_STAGES } from '../model/stages'
import { canBackToQuote } from './back-to-quote-eligibility'
import { invalidateShipmentLists } from './invalidate-shipment-queries'
import { STAGE_ROUTE } from './stage-route'

/** 400 races (someone advanced or posted the order in another tab). Their
 *  `message` names the actual state, so it is safe to show as-is. */
const RACE_ERROR_CODES = new Set(['NOT_NEW_ORDER', 'ACTIVE_DISPATCH'])

/** Back to Quotes needs `order.update` + `quote.create`; when either grant is
 *  `own`-scoped the caller must also be the assignee (the API 403s otherwise). */
export function useCanBackToQuote(
  shipment: ShipmentDetail | undefined,
): boolean {
  const ability = useAbility()
  const scopeOf = useScopeOf()
  const currentUserId = useAuthStore((state) => state.user?.id)
  if (!canBackToQuote(shipment)) return false
  if (!ability.can('update', 'order') || !ability.can('create', 'quote')) {
    return false
  }
  const ownScoped =
    scopeOf('order', 'update') === 'own' || scopeOf('quote', 'create') === 'own'
  return !ownScoped || shipment?.user?.id === currentUserId
}

export function useBackToQuote() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const mutation = useBackToQuoteApiV1ShipmentsShipmentIdBackToQuotePost({
    mutation: {
      onSuccess: (response, variables) => {
        queryClient.setQueryData(
          getGetShipmentApiV1ShipmentsShipmentIdGetQueryKey(
            variables.shipmentId,
          ),
          response,
        )
        invalidateShipmentLists(queryClient)
        toast.success(t('detailToolbar.backToQuote.success'))
        const route = STAGE_ROUTE[SHIPMENT_STAGES.QUOTE]
        void navigate({
          to: route.to,
          params: { [route.paramKey]: variables.shipmentId } as never,
        })
      },
      onError: (error) => {
        const code = getApiErrorCode(error)
        const raceMessage =
          code !== null && RACE_ERROR_CODES.has(code)
            ? getApiErrorDetail(error)
            : null
        toast.error(raceMessage ?? t('detailToolbar.backToQuote.error'))
      },
    },
  })

  return {
    backToQuote: (shipmentId: string, status: QuoteStatus) => {
      if (mutation.isPending) return
      mutation.mutate({ shipmentId, data: { status } })
    },
    isPending: mutation.isPending,
  }
}
